<template>
  <div :class="{'pt-2': modal}">
    <div v-if="$store.getters.isLayoutAndExtendedCateringFlow" class="mb-4" :class="{'mb-8': modal}" v-auto-animate>
      <FormLabel class="mb-2">
        Catering and drinks preferences
      </FormLabel>

      <RadioField
        v-model="form.catering.requireCatering.value"
        class="mb-2"
        label="I need venue to provide catering or drinks"
        name="catering"
        :val="true"
        :checked="form.catering.requireCatering.value === 1"
        @update:modelValue="(val) => {
          form.catering.requireCatering.errors = [];
          track('catering_required', 'change', val);
        }"
      />
      <div v-if="form.catering.requireCatering.value">
        <div class="ml-6 mb-4 mt-3 grid grid-cols-2 gap-y-2">
          <CheckboxField
            v-for="option in extendedCateringOptionsList"
            v-model="form.extendedCatering[option.value].value"
            :key="option.value"
            :label="option.label"
            :data-test-id="`enquiry-form__catering-option-${option.value}`"
            :name="option.value"
            :value="option.value"
            @update:modelValue="changeExtendedCateringOption(option)"
          />
        </div>
      </div>
      <FormCaption
        v-if="showVenueCateringError"
        type="invalid"
        class="mt-0 mb-3"
      >
        Venue does not provide catering services
      </FormCaption>

      <RadioField
        v-model="form.catering.ownCatering.value"
        class="mb-2"
        label="I will bring my own catering"
        name="catering"
        :val="true"
        @update:modelValue="(val) => {
          form.catering.requireCatering.errors = [];
          track('own_catering', 'change', val);
        }"
      />
      <FormCaption
        v-if="showOwnCateringError"
        type="invalid"
        class="mt-3 mb-3"
      >
        Venue does not allow external catering
      </FormCaption>

      <RadioField
        v-model="form.catering.requireCatering.value"
        class="mb-2"
        label="I don’t plan to have catering"
        name="catering"
        :val="0"
        @update:modelValue="(val) => {
          form.catering.requireCatering.errors = [];
          form.catering.ownCatering.value = false;
          track('no_catering', 'change', val)
        }"
      />

      <FormCaption
        v-if="form.catering.requireCatering.errors.length"
        type="invalid"
        class="mt-1 mb-2"
      >
        {{ form.catering.requireCatering.errors[0] }}
      </FormCaption>
    </div>

    <FormLabel
      class="mb-2"
      for="enquiry-form-message"
    >
      {{ messageLabel }}
    </FormLabel>
    <TextareaField
      id="enquiry-form-message"
      ref="message"
      v-model="form.message.value"
      class="mb-4"
      :rows="4"
      :errors="form.message.errors"
      placeholder="Please write what you'd like to use the space for and any questions you might have"
      data-test-id="enquiry-form__message"
      @update:modelValue="form.message.errors = [];"
      @click="track('message_box', 'click')"
    />

    <template v-if="!$store.getters.isLayoutAndExtendedCateringFlow">
      <CheckboxField
        v-model="form.catering.requireCatering.value"
        class="mb-2"
        name="requireCatering"
        label="I require catering"
        data-test-id="enquiry-form__catering"
        @update:modelValue="(val) => track('catering_required', 'change', val)"
      />
      <div
        v-if="form.catering.requireCatering.value && !showVenueCateringError"
        class="ml-6 mt-2 flex flex-col sm:flex-row"
      >
        <div class="mr-2">
          <CheckboxField
            v-for="option in cateringOptions.slice(0, 3)"
            v-model="form.catering[option.value].value"
            :key="option.value"
            :label="option.label"
            :data-test-id="`enquiry-form__catering-option-${option.value}`"
            :name="option.value"
            class="mb-2"
            @update:modelValue="changeCateringOption"
          />
        </div>
        <div>
          <CheckboxField
            v-for="option in cateringOptions.slice(3)"
            v-model="form.catering[option.value].value"
            :key="option.value"
            :label="option.label"
            :data-test-id="`enquiry-form__catering-option-${option.value}`"
            :name="option.value"
            class="mb-2"
            @update:modelValue="changeCateringOption"
          />
        </div>
      </div>

      <FormCaption
        v-if="showVenueCateringError"
        type="invalid"
        class="mt-1 ml-0.5 mb-2"
      >
        Venue does not provide catering services
      </FormCaption>

      <CheckboxField
        v-model="form.catering.ownCatering.value"
        class="mt-1 mb-2"
        label="I want my own catering"
        name="ownCatering"
        @update:modelValue="(val) => track('own_catering', 'change', val)"
      />

      <FormCaption
        v-if="showOwnCateringError"
        type="invalid"
        class="mt-1 ml-0.5"
      >
        Venue does not allow external catering
      </FormCaption>
    </template>
  </div>
</template>

<script>
import CheckboxField from '@/components/Ui/Forms/CheckboxField.vue';
import FormCaption from '@/components/Ui/Forms/FormCaption.vue';
import FormLabel from '@/components/Ui/Forms/FormLabel.vue';
import RadioField from '@/components/Ui/Forms/RadioField.vue';
import TextareaField from '@/components/Ui/Forms/TextareaField.vue';
import {
  extendedOptionsList as extendedCateringOptionsList,
  optionsList as cateringOptions,
} from '@/modules/public/enquiry-form/misc/cateringOptions.js';
import formValidation from '@/services/formValidation/index.js';

export default {
  name: 'MessageAndCatering',
  mixins: [formValidation],
  components: { RadioField, FormLabel, FormCaption, CheckboxField, TextareaField },
  props: {
    modal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const form = this.$store.state.form.messageAndCatering;

    const catering = Object.keys(form.catering).reduce((acc, cateringKey) => ({
      ...acc,
      [cateringKey]: {
        value: form.catering[cateringKey],
        errors: [],
      },
    }), {});

    const extendedCatering = Object.keys(form.extendedCatering).reduce((acc, cateringKey) => ({
      ...acc,
      [cateringKey]: {
        value: form.extendedCatering[cateringKey],
        errors: [],
      },
    }), {});

    return {
      form: {
        message: {
          value: form.message,
          errors: [],
        },
        catering,
        extendedCatering,
      },
      formValidation: {},
      cateringOptions,
      extendedCateringOptionsList,
    };
  },

  computed: {
    messageLabel() {
      return `Message to ${this.$store.state.manager.shortName}`;
    },
    showVenueCateringError() {
      return this.form.catering.requireCatering.value && !this.$store.state.room.catering.availableByVenue;
    },
    showOwnCateringError() {
      return this.form.catering.ownCatering.value && !this.$store.state.room.catering.availableExternal;
    },
  },

  watch: {
    form: {
      handler() {
        this.$store.commit('setMessageAndCateringForm', this.form, this.$store.getters.isLayoutAndExtendedCateringFlow);
      },
      deep: true,
    },
    'form.catering.requireCatering.value': function (value) {
      if (value) {
        this.form.catering.ownCatering.value = false;
      }
    },
    'form.catering.ownCatering.value': function (value) {
      if (value) {
        this.form.catering.requireCatering.value = false;
      }
    },
  },

  methods: {
    submit() {
      const isValid = this.validateFields() && !this.showOwnCateringError && !this.showVenueCateringError;
      if (isValid) {
        this.$store.commit('setMessageAndCateringForm', this.form, this.$store.getters.isLayoutAndExtendedCateringFlow);
      }
      return { isValid };
    },
    changeCateringOption() {
      const options = [];
      for (const option of this.cateringOptions) {
        if (this.form.catering[option.value].value) {
          options.push(option.value);
        }
      }

      this.track('catering_required_options', 'change', options);
    },
    changeExtendedCateringOption(element) {
      const options = [];
      for (const option of this.extendedCateringOptionsList) {
        if (this.form.extendedCatering[option.value].value) {
          options.push(option.value);
        }
      }
      this.track('catering_required_options', 'change', options);

      if (this.form.catering?.[element.value]) {
        this.form.catering[element.value].value = this.form.extendedCatering[element.value].value;
      }
    },
    track(element, interaction, value = null) {
      this.$emit('track', {
        element,
        interaction,
        value,
      });
    },

    customFieldValidate(fieldName) {
      if (this.$store.getters.isLayoutAndExtendedCateringFlow && fieldName === 'extendedCatering') {
        if (this.form.catering.requireCatering.value === false && this.form.catering.ownCatering.value === false) {
          this.form.catering.requireCatering.errors.push('Please select your catering preference');
          return false;
        }
      }
      return true;
    },
  },

  mounted() {
    $(this.$refs.message.$el.querySelector('textarea')).textareaAutoSize();
  },
};
</script>

<style lang="scss" scoped>
:deep(#enquiry-form-message) {
  min-height: 90px;
  max-height: 270px;
}
</style>
